import React, { FormEvent, useMemo, useState } from "react";
import { Button, CircularProgress, Dialog } from "@mui/material";
import InvoiceSearchableSelect from "../Invoicing/InvoiceSearchableSelect";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDialogPaperSx,
} from "../Invoicing/InvoiceDesignSystem";
import {
  prospectDialogCloseIconOptions,
  prospectGoldButtonSx,
  prospectInputClassName,
  prospectNoOptionsSx,
  prospectOutlinedButtonSx,
  prospectSelectMenuSx,
  prospectSelectSx,
} from "./prospectDesign";
import {
  getProspectErrorMessage,
  getProspectFieldLengthMessage,
  PROSPECT_FIELD_MAX_LENGTHS,
} from "./prospectFormModel";
import { buildStaffErrorFallback } from "../../../utils/userFacingError";

export type ProspectLeadCaptureOption = { id: string; label: string };
type LeadCaptureSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: readonly ProspectLeadCaptureOption[];
  onCreateLeadCapture: (label: string) => Promise<ProspectLeadCaptureOption>;
  error?: string;
  loading?: boolean;
  disabled?: boolean;
};

export const LeadCaptureSelect = ({
  value,
  onChange,
  options,
  onCreateLeadCapture,
  error,
  loading = false,
  disabled = false,
}: LeadCaptureSelectProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newLabelError, setNewLabelError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [locallyCreatedOptions, setLocallyCreatedOptions] = useState<
    ProspectLeadCaptureOption[]
  >([]);
  const availableOptions = useMemo(() => {
    const optionsById = new Map<string, ProspectLeadCaptureOption>();
    [...options, ...locallyCreatedOptions].forEach((option) =>
      optionsById.set(option.id, option),
    );
    return Array.from(optionsById.values());
  }, [locallyCreatedOptions, options]);

  const handleClose = () => {
    if (isSaving) return;
    setModalOpen(false);
    setNewLabel("");
    setNewLabelError("");
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isSaving) return;
    const trimmedLabel = newLabel.trim();
    if (!trimmedLabel) {
      setNewLabelError("Enter a lead capture name.");
      return;
    }
    if (trimmedLabel.length > PROSPECT_FIELD_MAX_LENGTHS.leadCaptureLabel) {
      setNewLabelError(
        getProspectFieldLengthMessage(
          "Lead capture name",
          PROSPECT_FIELD_MAX_LENGTHS.leadCaptureLabel,
        ),
      );
      return;
    }
    if (
      availableOptions.some(
        (option) =>
          option.label.trim().toLowerCase() === trimmedLabel.toLowerCase(),
      )
    ) {
      setNewLabelError(
        "A lead capture with this name already exists. Close this window and select it from the Lead Capture list.",
      );
      return;
    }
    setIsSaving(true);
    setNewLabelError("");
    try {
      const createdOption = await onCreateLeadCapture(trimmedLabel);
      setLocallyCreatedOptions((current) => [...current, createdOption]);
      onChange(createdOption.id);
      setModalOpen(false);
      setNewLabel("");
    } catch (error) {
      setNewLabelError(
        getProspectErrorMessage(error, buildStaffErrorFallback("save the lead capture")),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <label
        id="prospect-lead-capture-label"
        htmlFor="prospect-lead-capture"
        className="mb-2 block text-[15px] font-semibold leading-[normal] text-[#0A0A0A]"
      >
        Lead Capture<span className="text-[#E24C4C]"> *</span>
      </label>
      <InvoiceSearchableSelect
        id="prospect-lead-capture"
        labelledById="prospect-lead-capture-label"
        value={availableOptions.find((option) => option.id === value) || null}
        options={availableOptions}
        optionRowHeight={40}
        outlinedArrow
        getOptionKey={(option) => option.id}
        getOptionLabel={(option) => option.label}
        onChange={(option) => onChange(option?.id || "")}
        placeholder="Select Lead Capture"
        searchPlaceholder="Search Lead Capture"
        noOptionsText="No matches found"
        noOptionsSx={prospectNoOptionsSx}
        menuSx={{
          ...prospectSelectMenuSx,
          '& [role="listbox"] + div > button': { lineHeight: "normal" },
        }}
        loadingText="Loading lead captures..."
        loading={loading}
        disabled={disabled || loading}
        error={!!error}
        helperText={error}
        sx={{
          ...prospectSelectSx,
          "& .MuiFormHelperText-root": { mt: "8px", lineHeight: "normal" },
          "&& .MuiFormHelperText-root.Mui-error": { color: "#E24C4C !important" },
        }}
        required
        footerAction={{
          label: "Add New Lead Capture",
          onClick: () => {
            setNewLabel("");
            setNewLabelError("");
            setModalOpen(true);
          },
        }}
      />
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        aria-labelledby="prospect-new-lead-capture-title"
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(10,10,10,.45)" } },
        }}
        PaperProps={{
          sx: {
            ...invoiceDialogPaperSx({ maxWidth: 560 }),
            border: "1px solid #ECECEC",
            width: "min(560px, 92vw)",
            margin: 0,
          },
        }}
      >
        <form
          noValidate
          onSubmit={handleCreate}
          style={{ fontFamily: invoiceDesignFontFamily }}
        >
          <InvoiceDialogCloseButton
            ariaLabel="Close Add New Lead Capture"
            outlinedIcon={prospectDialogCloseIconOptions}
            hoverBackgroundColor="#F21B1B"
            onClick={handleClose}
            disabled={isSaving}
          />
          <h2
            id="prospect-new-lead-capture-title"
            className="px-[26px] pt-[22px] text-[17px] font-semibold leading-[normal] text-[#0A0A0A]"
          >
            Add New Lead Capture
          </h2>
          <div className="border-b border-[#ECECEC] px-[26px] pb-[22px] pt-[18px]">
            <input
              autoFocus
              aria-label="Enter New Lead Capture"
              className={prospectInputClassName}
              value={newLabel}
              placeholder="Enter New Lead Capture"
              disabled={isSaving}
              aria-invalid={newLabelError ? true : undefined}
              aria-describedby={
                newLabelError ? "prospect-new-lead-capture-error" : undefined
              }
              style={{ borderColor: newLabelError ? "#E24C4C" : undefined }}
              onChange={(event) => {
                setNewLabel(event.target.value);
                setNewLabelError("");
              }}
            />
            {newLabelError ? (
              <p
                id="prospect-new-lead-capture-error"
                className="mt-1 text-[12px] leading-4 text-[#E24C4C]"
              >
                {newLabelError}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap justify-end gap-3 px-[26px] py-[18px]">
            <Button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              sx={{
                ...prospectOutlinedButtonSx,
                borderColor: "#ECECEC",
                padding: "11px 22px",
                "&:hover": { backgroundColor: "#F7F7F8", borderColor: "#ECECEC" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              sx={{ ...prospectGoldButtonSx, padding: "11px 22px" }}
              startIcon={
                isSaving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Save New Lead Capture
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
};
